/**
 * Where this booking happens (mobile grooming, SF 101–121): the visit type,
 * the address (a saved one when signed in, a Google place before), the
 * drop-off for a pick-up, and the server's quote. One per booking, kept for
 * the tab (sessionStorage) so a reload or Stripe's return keeps it, and
 * forgotten at sign-out (G1) and once booked.
 *
 * Null is the salon, as every booking was before: nothing here is sent.
 */
import { useSyncExternalStore } from 'react';
import type { ClientAddress, TravelQuote, VisitType } from '../types/mobile.types';

export const WHERE_KEY = 'booking_where_v1';

export interface PlaceChoice {
  placeId: string;
  /** "12 Example St, Sampleton VIC 3000". */
  text: string;
  sessionToken: string;
}

export interface WhereChoice {
  storeId: number;
  visitType: VisitType;
  /** Signed in: one of their addresses. */
  address?: ClientAddress | null;
  /** Signed out: a Google place, saved to them when they sign in. */
  place?: PlaceChoice | null;
  /** A pick-up brought back somewhere else (D117); null is the same address. */
  dropOff?: ClientAddress | null;
  /** The server's answer for this address (G42). */
  quote?: TravelQuote | null;
  /**
   * The day the quote was asked for ("2026-11-03"), when a zone's day price
   * makes the fee depend on it (mpgadmin D148); absent otherwise. Sent with
   * every later ask (the deals preview, back-about) so all agree.
   */
  day?: string | null;
  /** A pick-up: when they're home again for the time picked, in the server's words (SF 112, 113; mpgadmin D117). */
  back?: { startUtc: string; timeline: string; collectUtc?: string | null } | null;
}

type State = { choice: WhereChoice | null; askedFor: number | null };

const read = (): State => {
  try {
    const raw = sessionStorage.getItem(WHERE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? { choice: parsed.choice ?? null, askedFor: parsed.askedFor ?? null } : { choice: null, askedFor: null };
  } catch {
    return { choice: null, askedFor: null };
  }
};

let state: State = read();
const listeners = new Set<() => void>();

const write = (next: State) => {
  state = next;
  try {
    if (next.choice || next.askedFor) sessionStorage.setItem(WHERE_KEY, JSON.stringify(next));
    else sessionStorage.removeItem(WHERE_KEY);
  } catch {
    /* private mode: kept in memory for the page */
  }
  listeners.forEach(l => l());
};

export const whereStore = {
  get: (): WhereChoice | null => state.choice,
  /** The location the sheet has been answered for this booking, so it isn't asked again on every return to the menu. */
  askedFor: (): number | null => state.askedFor,
  set(choice: WhereChoice | null) {
    write({ choice: choice && choice.visitType !== 0 ? choice : null, askedFor: choice?.storeId ?? state.askedFor });
  },
  patch(p: Partial<WhereChoice>) {
    if (state.choice) write({ ...state, choice: { ...state.choice, ...p } });
  },
  /** The sheet was answered "At the salon" for this location. */
  salon(storeId: number) {
    write({ choice: null, askedFor: storeId });
  },
  /** "Change" after the menu (the time step, Review): the sheet opens again on the menu, the choice kept. */
  askAgain() {
    write({ ...state, askedFor: null });
  },
  /** Booked, signed out, or another location: start again. */
  forget() {
    write({ choice: null, askedFor: null });
  },
  subscribe(l: () => void) {
    listeners.add(l);
    return () => listeners.delete(l);
  },
};

/** The choice for this location, or null (the salon, or another location's). */
export function useWhere(storeId: number | null | undefined): WhereChoice | null {
  const choice = useSyncExternalStore(whereStore.subscribe, () => state.choice);
  return choice && choice.storeId === storeId ? choice : null;
}

/** Whether the where sheet has been answered for this location this booking. */
export function useWhereAsked(storeId: number | null | undefined): boolean {
  return useSyncExternalStore(whereStore.subscribe, () => state.askedFor) === storeId;
}
